// Shared logic for matching a patient's free-text "Custom / Other Substance" ADR entries
// against a drug being selected, used by PrescriptionChart's allergy cross-check.
//
// A raw two-way `.includes()` substring match (`customText.includes(drugText) ||
// drugText.includes(customText)`) over-warns badly: a short free-text entry like "sul",
// "ace", or a stray single character would substring-match a large set of unrelated drugs.
// Over-warning on a clinical safety modal causes alert fatigue, which is its own safety
// problem, so partial matches are restricted to a minimum length and required to occur on a
// word boundary rather than as an arbitrary substring.

// Minimum length either string must be before a *partial* match is considered. An exact
// match is always honoured regardless of length - if the free-text entry literally equals
// the drug/generic name, that's a real match no matter how short either string is.
export const MIN_ALLERGY_PARTIAL_MATCH_LENGTH = 4;

function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// True if `customText` (a patient's free-text allergy entry) should be treated as matching
// `drugText` (a generic or brand drug name), both already trimmed/lowercased by the caller.
export function isAllergyTextMatch(customText, drugText) {
    if (!customText || !drugText) return false;
    if (customText === drugText) return true;
    if (
        customText.length < MIN_ALLERGY_PARTIAL_MATCH_LENGTH ||
        drugText.length < MIN_ALLERGY_PARTIAL_MATCH_LENGTH
    ) {
        return false;
    }
    // Word-boundary match: the shorter string must occur as a whole word within the longer
    // one (e.g. custom "penicillin" vs drug "amoxicillin/penicillin combo"), not merely as an
    // arbitrary substring (e.g. custom "cort" should not match drug "cortisone" as a false
    // positive prefix when they're otherwise unrelated free-text entries).
    const shorter = customText.length <= drugText.length ? customText : drugText;
    const longer = customText.length <= drugText.length ? drugText : customText;
    return new RegExp(`\\b${escapeRegExp(shorter)}\\b`).test(longer);
}
